import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsService, prospectService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import BackgroundVideo from '../components/BackgroundVideo';
import Hero from '../components/Hero';
import ProgramShowcase from '../components/ProgramShowcase';
import StudentExperience from '../components/StudentExperience';
import LegacyTimeline from '../components/LegacyTimeline';
import { env } from '../env';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [hideNav, setHideNav] = useState(false);
  const [isLegacyVisible, setIsLegacyVisible] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const legacyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const target = legacyRef.current;
    if (!container || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === target) {
            setHideNav(entry.isIntersecting);
          }
        });
      },
      { root: container, threshold: 0.6 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    const container = scrollContainerRef.current;
    const target = legacyRef.current;
    if (!container || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === target) {
            setIsLegacyVisible(entry.isIntersecting);
          }
        });
      },
      { root: container, threshold: 0.2 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  
  // Function to handle manual redirect on click
  const handleRedirect = () => {
    if (isAuthenticated && user) {
      // Redirect based on current user type
  if (user.is_admin) navigate('/admin/dashboard'); else if (user.role === 'professor') navigate('/professor-pathway'); else navigate('/student-pathway');
    } else {
      navigate('/login');
    }
  };
  
  // Array of available videos
  const videos = (() => {
    const configured = env.REACT_APP_HERO_VIDEOS;
    if (configured) {
      return configured
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }
    return [
      '/videos/0.mp4',
      '/videos/1.mp4',
      '/videos/2.mp4',
      '/videos/103.mp4',
    ];
  })();

  const handleFormSubmit = async (formData: {first_name: string, last_name: string, email: string}) => {
    setLoading(true);
    setError('');

    try {
      const response = await prospectService.createProspect(formData);
      setSuccess(true);
      analyticsService.trackEvent({
        event_name: 'lead_capture_submit',
        page_type: 'home',
        payload: {
          source: 'hero_form',
        },
      }).catch(() => {
        // Ignore analytics errors for lead capture.
      });
      
      // If we got account credentials, store them to show to user and auto-login
      if (response.temporary_password) {
        try {
          const loginSuccess = await login(formData.email, response.temporary_password);
          if (loginSuccess) {
            navigate('/lead-thanks');
            return;
          }
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
        }
      }
      navigate('/login');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {/* Background Video Component - fade out before legacy section */}
      <div
        className={`fixed inset-0 w-screen h-screen overflow-hidden z-0 transition-opacity duration-700 ${
          isLegacyVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <BackgroundVideo videos={videos} overlayOpacity={0.4} randomize={false} />
      </div>

      {/* Navbar - Fixed at top */}
      <div
        className={`fixed top-0 left-0 w-full z-50 transition-opacity duration-300 ${
          hideNav ? 'opacity-0 pointer-events-none invisible' : 'opacity-100 visible'
        }`}
      >
        <Navbar />
      </div>

      {/* Main Content Container - Scrollable sections */}
      <div
        className="relative z-10 h-screen overflow-y-auto snap-y snap-mandatory"
        ref={scrollContainerRef}
      >
        {/* Hero Section */}
        <div id="hero" className="min-h-screen pt-20 snap-start">
          <Hero
            onFormSubmit={handleFormSubmit}
            loading={loading}
            success={success}
            error={error}
            onRedirect={handleRedirect}
          />
        </div>

        {/* Program Showcase */}
        <div className="snap-start">
          <ProgramShowcase />
        </div>

        {/* CIUE: Student Experience sections */}
        <div className="snap-start">
          <StudentExperience />
        </div>

        {/* Legacy Timeline */}
        <div className="snap-start" ref={legacyRef}>
          <LegacyTimeline />
        </div>
      </div>
    </div>
  );
};

export default Home;
